// components/notifications/invitation/JoinRequestNotificationCard.jsx
import React, { useState } from 'react';
import {
  Box, Avatar, Typography, Link as MuiLink, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { PersonAdd as PersonAddIcon, Circle as CircleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import { useUser } from 'shared/helpers/axios';

// Helper to compute initials from a name
const initialsFromName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + second).toUpperCase();
};

function JoinRequestNotificationCard({
  notification,
  onMarkRead,
  handleNotificationAction,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useUser();

  const payload = notification.payload || {};
  // join_request_id is the ConsortiumInvitation UUID (no dashes).
  // Fall back to reference_id (with dashes) only when the payload field is absent.
  // Both resolve to the same object in the backend's get_object normalizer.
  if (!payload.join_request_id && process.env.NODE_ENV !== 'production') {
    console.warn('JoinRequestNotificationCard: payload.join_request_id missing, using reference_id fallback', notification);
  }
  const joinRequestId = payload.join_request_id || notification.reference_id;
  const consortiumId = payload.consortium_id;
  const consortiumName = payload.consortium_name || 'Unknown Consortium';
  const requesterName = payload.requester_name || 'Someone';
  const requesterId = payload.requester_id;
  const requesterAvatarUrl = payload.requester_avatar_url || null;
  const requesterInitials = initialsFromName(requesterName);

  const coordinatorName = payload.coordinator_name;
  const coordinatorId = payload.coordinator_id;
  const coordinatorAvatarUrl = payload.coordinator_avatar_url || null;
  const coordinatorInitials = initialsFromName(coordinatorName || '');

  const [imgBroken, setImgBroken] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const status = (payload.status || '').toString().toLowerCase();
  const isPending = status === 'pending' || status === '';
  const isAccepted = status === 'accepted';
  const isRejected = status === 'rejected';

  // In accepted/rejected state the actor shown depends on perspective:
  // - requester's view (coordinator fields present): coordinator acted → show coordinator avatar
  // - coordinator's view (no coordinator fields): requester is the subject → show requester avatar
  const isCoordinatorPerspective = !!(coordinatorName && coordinatorId);
  const actorAvatarUrl = (isAccepted || isRejected) && isCoordinatorPerspective
    ? coordinatorAvatarUrl
    : requesterAvatarUrl;
  const actorInitials = (isAccepted || isRejected) && isCoordinatorPerspective
    ? coordinatorInitials
    : requesterInitials;
  const showInitials = !actorAvatarUrl || imgBroken;

  // Don't show action buttons to the requester (they sent the request, can't accept/reject their own)
  const isRequester = currentUser && requesterId &&
    String(currentUser.id) === String(requesterId);
  // Don't show action buttons if not pending OR if currently processing an action OR if viewer is the requester
  const showActionButtons = isPending && !actionLoading && !isRequester;

  const consortiumUrl = consortiumId ? `/consortium/${consortiumId}` : null;
  const requesterProfileUrl = requesterId ? `/profile/${requesterId}` : null;


  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [message, setMessage] = useState('');

  const handleMarkRead = () => {
    if (!notification.is_read && onMarkRead) {
      onMarkRead();
    }
  };

  const handleRequesterClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleMarkRead();
    if (requesterProfileUrl) {
      navigate(requesterProfileUrl);
    }
  };

  const handleCardClick = () => {
    handleMarkRead();
    if (consortiumUrl) {
      navigate(consortiumUrl);
    }
  };

  const handleAccept = async (e) => {
    e.stopPropagation();
    setActionLoading(true);
    try {
      await handleNotificationAction(notification.id, { 
        action: 'accept_join_request', 
        meta: { id: joinRequestId } 
      });
      
    } catch (error) {
      console.error('Failed to accept join request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (e) => {
    e.stopPropagation();
    setActionLoading(true);
    try {
      await handleNotificationAction(notification.id, { 
        action: 'reject_join_request', 
        meta: { id: joinRequestId } 
      });
    } catch (error) {
      console.error('Failed to reject join request:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    setActionLoading(true);
    try {
      // TODO: Implement message sending functionality
      // This could create a direct message or add a comment
      console.log('Sending message:', message, 'to user:', requesterId);
      setMessageDialogOpen(false);
      setMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const getMessage = () => {
    const coordinatorName = payload.coordinator_name;
    const coordinatorId = payload.coordinator_id;
    
    if (isPending) {
      return (
        <>
          has requested to join{' '}
          <Box component="span" fontWeight={500} color="text.primary">
            {consortiumName}
          </Box>
        </>
      );
    }
    if (isAccepted) {
      // Check if this is a notification to the coordinator (they accepted) or requester (they were accepted)
      if (coordinatorName && coordinatorId) {
        // Notification to requester: "Coordinator accepted your request"
        return (
          <>
            <Box component="span" fontWeight={500} color="text.primary">
              {coordinatorName}
            </Box>
            {' '}accepted your request to join{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {consortiumName}
            </Box>
          </>
        );
      } else {
        // Notification to coordinator: "You accepted X's request"
        return (
          <>
            You accepted{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {requesterName}
            </Box>
            's request to join{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {consortiumName}
            </Box>
          </>
        );
      }
    }
    if (isRejected) {
      // Check if this is a notification to the coordinator (they rejected) or requester (they were rejected)
      if (coordinatorName && coordinatorId) {
        // Notification to requester: "Coordinator rejected your request"
        return (
          <>
            <Box component="span" fontWeight={500} color="text.primary">
              {coordinatorName}
            </Box>
            {' '}rejected your request to join{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {consortiumName}
            </Box>
          </>
        );
      } else {
        // Notification to coordinator: "You rejected X's request"
        return (
          <>
            You rejected{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {requesterName}
            </Box>
            's request to join{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {consortiumName}
            </Box>
          </>
        );
      }
    }
    return `Request to join ${consortiumName}`;
  };

  // Render simple notification for non-pending requests OR when action is loading
  if (!showActionButtons) {
    return (
      <Box
        onClick={handleCardClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
          transition: 'background-color 0.2s',
          cursor: consortiumUrl ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: 'action.selected',
          },
        }}
      >
        <Avatar
          src={showInitials ? undefined : actorAvatarUrl}
          imgProps={{ onError: () => setImgBroken(true) }}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: theme.palette.primary.avatar,
            color: 'white',
            cursor: requesterProfileUrl ? 'pointer' : 'default',
          }}
          onClick={requesterProfileUrl ? handleRequesterClick : undefined}
        >
          {showInitials ? actorInitials : <PersonAddIcon fontSize="small" />}
        </Avatar>

        <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {actionLoading ? (
            <Box component="span" color="text.secondary" fontStyle="italic">
              Processing...
            </Box>
          ) : (
            <>
              {isPending && (
                requesterProfileUrl ? (
                  <MuiLink
                    component="a"
                    href={requesterProfileUrl}
                    underline="hover"
                    color="text.primary"
                    fontWeight={600}
                    onClick={handleRequesterClick}
                    sx={{ cursor: 'pointer' }}
                  >
                    {requesterName}
                  </MuiLink>
                ) : (
                  <Box component="span" fontWeight={600}>{requesterName}</Box>
                )
              )}
              <Box component="span" color="text.secondary">
                {getMessage()}
              </Box>
            </>
          )}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
          </Typography>
          {!notification.is_read && (
            <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
          )}
        </Box>
      </Box>
    );
  }

  // Pending join request - show action buttons
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: 'action.selected',
          },
        }}
      >
        <Avatar
          src={showInitials ? undefined : requesterAvatarUrl}
          alt={requesterName || 'User'}
          imgProps={{ onError: () => setImgBroken(true) }}
          sx={{
            width: 32,
            height: 32,
            
            backgroundColor: theme.palette.primary.avatar,
            color: 'white',

            cursor: requesterProfileUrl ? 'pointer' : 'default',
          }}
          onClick={requesterProfileUrl ? handleRequesterClick : undefined}
        >
          {showInitials ? requesterInitials : <PersonAddIcon fontSize="small" />}
        </Avatar>

        <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {requesterProfileUrl ? (
            <MuiLink
              component="a"
              href={requesterProfileUrl}
              underline="hover"
              color="text.primary"
              fontWeight={600}
              onClick={handleRequesterClick}
              sx={{ cursor: 'pointer' }}
            >
              {requesterName}
            </MuiLink>
          ) : (
            <Box component="span" fontWeight={600}>
              {requesterName}
            </Box>
          )}
          <Box component="span" color="text.secondary">
            {getMessage()}
          </Box>
        </Typography>

        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={handleAccept}
            disabled={actionLoading}
            sx={{ minWidth: 70 }}
          >
            Accept
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
            sx={{ minWidth: 70 }}
          >
            Reject
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              setMessageDialogOpen(true);
            }}
            disabled={actionLoading}
            sx={{ minWidth: 90 }}
          >
            Message
          </Button>
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
          </Typography>
          {!notification.is_read && (
            <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
          )}
        </Box>
      </Box>

      {/* Message Dialog */}
      <Dialog 
        open={messageDialogOpen} 
        onClose={() => setMessageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Message to {requesterName}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendMessage} 
            variant="contained" 
            disabled={actionLoading || !message.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default JoinRequestNotificationCard;
